import { useEffect, useRef, useState } from "react";

/**
 * useInView — small IntersectionObserver hook.
 *
 * Returns [ref, inView]. `inView` flips to true ONCE when the element
 * enters the viewport and stays true. Used to trigger entrance
 * animations only when the user actually scrolls to the element.
 *
 * Options:
 *   threshold:  0..1, default 0.15 (15% of element visible)
 *   rootMargin: default "0px 0px -80px 0px" (fires a bit before fully on-screen)
 *   once:       default true — unfreezes after first reveal
 */
const useInView = (options = {}) => {
  const {
    threshold  = 0.15,
    rootMargin = "0px 0px -80px 0px",
    once       = true,
  } = options;

  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Respect reduced-motion: skip the observer & render immediately
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
};

export default useInView;
